import React, { useState } from 'react';
import { View, Text, StyleSheet, Alert, Pressable, FlatList } from 'react-native';
import { useTranslation } from 'react-i18next';
import { linkSupplyToProject, createSupplyAndLinkToProject, SupplyNotFoundError } from '../../features/projects/linkSupply';
import { parseLocaleNumber } from '../shared/parseLocaleNumber';
import { formatLocaleNumber } from '../shared/formatLocaleNumber';
import type { AppDatabase } from '../../db/outbox';
import type { Supply } from '../../db/schema';
import type { ProjectSupplyView } from '../../features/projects/projectRepository';
import { colors, fonts, radii, spacing } from '../theme';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';
import { Chip } from '../shared/Chip';

interface Props {
  db: AppDatabase;
  userId: string;
  projectId: string;
  links: ProjectSupplyView[];
  catalog: Supply[];
  onLinked: () => void;
}

type Mode = 'closed' | 'pickExisting' | 'createNew';

/**
 * Seção "insumos" da tela de detalhe do projeto: lista o que já está
 * vinculado (com o preço da ÉPOCA — `unitPriceSnapshot`, nunca o preço
 * atual do catálogo, ver ProjectDetailScreen) e oferece adicionar um
 * insumo novo — do catálogo (sem redigitar preço) ou criado na hora
 * (entra no catálogo automaticamente). Extraído da tela de detalhe porque
 * o fluxo de duas abas (catálogo/novo) cresceria demais dentro dela.
 */
export function ProjectSuppliesSection({ db, userId, projectId, links, catalog, onLinked }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('closed');
  const [search, setSearch] = useState('');
  const [selectedSupplyId, setSelectedSupplyId] = useState<string | null>(null);
  const [quantityText, setQuantityText] = useState('');
  const [newType, setNewType] = useState('');
  const [newName, setNewName] = useState('');
  const [newUnit, setNewUnit] = useState('');
  const [newUnitPriceText, setNewUnitPriceText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const totalMaterialsCost = links.reduce((sum, link) => sum + link.quantity * link.unitPriceSnapshot, 0);

  const resetForm = () => {
    setMode('closed');
    setSearch('');
    setSelectedSupplyId(null);
    setQuantityText('');
    setNewType('');
    setNewName('');
    setNewUnit('');
    setNewUnitPriceText('');
  };

  const filteredCatalog = catalog.filter((supply) =>
    supply.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const handleConfirmExisting = async () => {
    const quantity = parseLocaleNumber(quantityText);
    if (!selectedSupplyId) {
      Alert.alert(t('project.supplyLink.errorTitle'), t('project.supplyLink.selectSupplyMessage'));
      return;
    }
    if (quantity === null || quantity <= 0) {
      Alert.alert(t('project.supplyLink.errorTitle'), t('project.supplyLink.invalidQuantityMessage'));
      return;
    }

    setSubmitting(true);
    try {
      await linkSupplyToProject(db, { projectId, supplyId: selectedSupplyId, quantity });
      resetForm();
      onLinked();
    } catch (error) {
      const message =
        error instanceof SupplyNotFoundError ? t('project.supplyLink.notFoundError') : (error as Error).message;
      Alert.alert(t('project.supplyLink.errorTitle'), message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmNew = async () => {
    const quantity = parseLocaleNumber(quantityText);
    const unitPrice = parseLocaleNumber(newUnitPriceText);

    if (!newType.trim() || !newName.trim() || !newUnit.trim() || unitPrice === null) {
      Alert.alert(t('project.supplyLink.errorTitle'), t('project.supplyLink.requiredFieldsMessage'));
      return;
    }
    if (quantity === null || quantity <= 0) {
      Alert.alert(t('project.supplyLink.errorTitle'), t('project.supplyLink.invalidQuantityMessage'));
      return;
    }

    setSubmitting(true);
    try {
      await createSupplyAndLinkToProject(db, {
        userId,
        projectId,
        quantity,
        type: newType.trim(),
        name: newName.trim(),
        unit: newUnit.trim(),
        unitPrice,
      });
      resetForm();
      onLinked();
    } catch (error) {
      const message =
        error instanceof SupplyNotFoundError ? t('project.supplyLink.notFoundError') : (error as Error).message;
      Alert.alert(t('project.supplyLink.errorTitle'), message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{t('project.detail.suppliesTitle')}</Text>

      {links.length === 0 ? (
        <Text style={styles.muted}>{t('project.detail.suppliesEmpty')}</Text>
      ) : (
        links.map((link) => (
          <View key={link.id} style={styles.supplyRow}>
            <Text style={styles.supplyName}>{link.supplyName}</Text>
            <Text style={styles.supplyMeta}>
              {link.quantity} {link.supplyUnit} × {t('common.currencyAmount', { value: formatLocaleNumber(link.unitPriceSnapshot) })}
              {' = '}
              {t('common.currencyAmount', { value: formatLocaleNumber(link.quantity * link.unitPriceSnapshot) })}
            </Text>
          </View>
        ))
      )}

      {links.length > 0 && (
        <Text style={styles.totalText}>
          {t('project.detail.suppliesTotalLabel')}:{' '}
          {t('common.currencyAmount', { value: formatLocaleNumber(totalMaterialsCost) })}
        </Text>
      )}

      {mode === 'closed' && (
        <Button title={t('project.detail.addSupplyButton')} variant="line" onPress={() => setMode('pickExisting')} />
      )}

      {mode !== 'closed' && (
        <View style={styles.form}>
          <View style={styles.tabRow}>
            <Chip label={t('project.supplyLink.existingTab')} active={mode === 'pickExisting'} onPress={() => setMode('pickExisting')} />
            <Chip label={t('project.supplyLink.newTab')} active={mode === 'createNew'} onPress={() => setMode('createNew')} />
          </View>

          {mode === 'pickExisting' ? (
            <>
              <TextField
                placeholder={t('project.supplyLink.searchPlaceholder')}
                value={search}
                onChangeText={setSearch}
              />
              <FlatList
                data={filteredCatalog}
                keyExtractor={(item) => item.id}
                style={styles.catalogList}
                ListEmptyComponent={<Text style={styles.muted}>{t('project.supplyLink.noResults')}</Text>}
                renderItem={({ item }) => (
                  <Pressable
                    onPress={() => setSelectedSupplyId(item.id)}
                    style={[styles.catalogItem, selectedSupplyId === item.id && styles.catalogItemSelected]}
                  >
                    <Text style={styles.catalogItemText}>
                      {item.name} — {t('common.currencyAmount', { value: formatLocaleNumber(item.unitPrice) })} / {item.unit}
                    </Text>
                  </Pressable>
                )}
              />
              <TextField
                placeholder={t('project.supplyLink.quantityPlaceholder')}
                value={quantityText}
                onChangeText={setQuantityText}
                keyboardType="decimal-pad"
              />
              <Button title={t('project.supplyLink.confirmButton')} onPress={handleConfirmExisting} disabled={submitting} />
            </>
          ) : (
            <>
              <TextField placeholder={t('supply.catalog.typeLabel')} value={newType} onChangeText={setNewType} />
              <TextField placeholder={t('supply.catalog.nameLabel')} value={newName} onChangeText={setNewName} />
              <TextField placeholder={t('supply.catalog.unitLabel')} value={newUnit} onChangeText={setNewUnit} />
              <TextField
                placeholder={t('supply.catalog.unitPriceLabel')}
                value={newUnitPriceText}
                onChangeText={setNewUnitPriceText}
                keyboardType="decimal-pad"
              />
              <TextField
                placeholder={t('project.supplyLink.quantityPlaceholder')}
                value={quantityText}
                onChangeText={setQuantityText}
                keyboardType="decimal-pad"
              />
              <Button title={t('project.supplyLink.confirmButton')} onPress={handleConfirmNew} disabled={submitting} />
            </>
          )}

          <Button title={t('project.detail.cancelAddSupplyButton')} variant="ghost" onPress={resetForm} disabled={submitting} />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  section: { width: '100%', marginTop: spacing.md },
  sectionTitle: { fontFamily: fonts.display, fontSize: 16, color: colors.ink, marginBottom: spacing.sm },
  muted: { fontFamily: fonts.body, color: colors.thread, marginBottom: spacing.sm },
  supplyRow: { paddingVertical: spacing.xs + 2, borderBottomWidth: 1, borderBottomColor: colors.line },
  supplyName: { fontFamily: fonts.bodyBold, fontSize: 15, color: colors.ink },
  supplyMeta: { fontFamily: fonts.body, fontSize: 13, color: colors.inkSoft },
  totalText: { fontFamily: fonts.bodyBold, fontSize: 14, color: colors.ink, marginVertical: spacing.sm },
  form: { marginTop: spacing.md },
  tabRow: { flexDirection: 'row' },
  catalogList: { maxHeight: 160, marginBottom: spacing.sm },
  catalogItem: { padding: spacing.sm, borderWidth: 1, borderColor: colors.line, borderRadius: radii.sm, marginBottom: spacing.xs },
  catalogItemSelected: { borderColor: colors.sageDeep, backgroundColor: colors.sandPanel },
  catalogItemText: { fontFamily: fonts.body, color: colors.ink },
});
